import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Banner } from '@osd/ui/banner';
import { Button } from '@osd/ui/button';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/banner — live docs for the `Banner` primitive.
 *
 * Banner is a full-width contextual notice for site-wide messages:
 * maintenance windows, feature announcements, rate-limit warnings.
 * For inline form-level messages use `<Alert>` instead.
 */
export const Route = createFileRoute('/ui/_layout/banner')({
  component: BannerPage,
});

function BannerPage() {
  return (
    <Stack gap={6}>
      <Section
        title="Banner"
        hint="Full-width contextual notice — title + body + optional action + optional dismiss"
      >
        <Text>
          Use Banner for site-wide messages that sit above the page content (maintenance windows,
          feature announcements, rate-limit warnings). For inline form-level messages use{' '}
          <code>&lt;Alert&gt;</code> instead.
        </Text>
      </Section>

      <Section title="Variants" hint="info / success / warning / danger">
        <Stack gap={3}>
          <Banner variant="info" title="New">
            We just shipped dark-mode support for the dashboard.
          </Banner>
          <Banner variant="success" title="Migration complete">
            All users have been moved to the new auth flow.
          </Banner>
          <Banner variant="warning" title="Scheduled maintenance">
            API will be unavailable 2026-10-01 02:00–04:00 UTC.
          </Banner>
          <Banner variant="danger" title="Outage detected">
            The /audit endpoint is returning 500s. Engineering is paged.
          </Banner>
        </Stack>
      </Section>

      <Section title="With CTA action" hint="Right-aligned slot for a button or link">
        <Stack gap={3}>
          <Banner
            variant="warning"
            title="Scheduled maintenance"
            action={<Button>View status</Button>}
          >
            API will be unavailable 2026-10-01 02:00–04:00 UTC.
          </Banner>
          <Banner variant="info" title="Try the new dashboard" action={<Button>Switch</Button>}>
            The new dashboard is faster and supports keyboard shortcuts.
          </Banner>
        </Stack>
      </Section>

      <Section title="Dismissible" hint="Toggle with state">
        <DismissibleDemo />
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Maintenance banner with CTA"
            code={`<Banner
  variant="warning"
  title="Scheduled maintenance"
  action={<Button>View status</Button>}
>
  API will be unavailable 2026-10-01 02:00–04:00 UTC.
</Banner>`}
          />
          <CodeBlock
            language="tsx"
            title="Dismissible banner with local state"
            code={`function MaintenanceBanner() {
  const [visible, setVisible] = useState(true);
  if (!visible) return null;
  return (
    <Banner
      variant="warning"
      title="Scheduled maintenance"
      dismissible
      onDismiss={() => setVisible(false)}
    >
      API will be unavailable tonight 02:00 UTC.
    </Banner>
  );
}`}
          />
        </Stack>
      </Section>
    </Stack>
  );
}

function DismissibleDemo(): JSX.Element {
  const [warning, setWarning] = useState(true);
  const [info, setInfo] = useState(true);
  return (
    <Stack gap={3}>
      {warning && (
        <Banner
          variant="warning"
          title="Dismiss me"
          dismissible
          onDismiss={() => setWarning(false)}
        >
          This banner hides itself via local state.
        </Banner>
      )}
      {info && (
        <Banner variant="info" title="And me" dismissible onDismiss={() => setInfo(false)}>
          Both banners use the same pattern.
        </Banner>
      )}
      {(!warning || !info) && (
        <Button
          onClick={() => {
            setWarning(true);
            setInfo(true);
          }}
        >
          Reset
        </Button>
      )}
    </Stack>
  );
}
